import React, { useRef } from 'react';
import {
  Table, FilterBuilder, Flex, FieldType
} from 'onefaceui-react-components';
import { useDispatch } from 'react-redux';

import DataSyncLogDetailsModal from 'react/containers/modals/DataSyncLogDetailsModal';

import { getResourceTo } from 'react/utils/utils';

import { actionCreators } from 'react/api/actions/modals';
import { getLocation, getTimeInputFormat } from 'react/utils/onefaceLibraryUtils';
import { Container } from './DataSyncLogs.styles';
import defaultFetch from 'react/api/sagas/fetch/defaultFetch';

export function DataSyncLogsList() {
  const dispatch = useDispatch();

  const tableRef = useRef(null);
  const filters = useRef({});

  const setFilters = (values) => {
    filters.current = values;
  };

  const tableColumns = [
    {
      title: getResourceTo('dataSyncConfiguration.fields.status'),
      field: 'statusProcess',
      width: 120
    },
    {
      title: getResourceTo('dataSyncLogs.fields.filesToProcess'),
      field: 'filesToProcess',
      width: 120
    },
    {
      title: getResourceTo('dataSyncLogs.fields.startProcessDate'),
      field: 'startProcessDate',
      formatter: (cell) => Monet.Format.DateTime.formatToLocale(cell.getValue())
    },
    {
      title: getResourceTo('dataSyncLogs.fields.endProcessDate'),
      field: 'endProcessDate',
      formatter: (cell) => Monet.Format.DateTime.formatToLocale(cell.getValue())
    },
    {
      title: getResourceTo('dataSyncLogs.fields.fileSize'),
      field: 'compressedSize',
      width: 100
    },
    {
      title: getResourceTo('dataSyncLogs.fields.compressedFileName'),
      field: 'compressedFileName'
    }
  ];

  const filterBuilderFields = [
    {
      id: 'startProcessDate',
      label: getResourceTo('genericStartInvertal'),
      name: 'startProcessDate',
      type: FieldType.DATETIME,
      extra: {
        timeFormat: getTimeInputFormat(Monet.Session.User.preferedLocale.timeFormat),
        location: getLocation(Monet.Session.User.preferedLocale.language)
      }
    },
    {
      id: 'endProcessDate',
      label: getResourceTo('reportGeneric.intervalEnd'),
      name: 'endProcessDate',
      type: FieldType.DATETIME,
      extra: {
        timeFormat: getTimeInputFormat(Monet.Session.User.preferedLocale.timeFormat),
        location: getLocation(Monet.Session.User.preferedLocale.language)
      }
    },
    {
      id: 'statusProcess',
      label: getResourceTo('dataSyncConfiguration.fields.status'),
      name: 'statusProcess',
      type: FieldType.SELECT,
      extra: {
        options: [
          {
            label: getResourceTo('eventLogHttpAction.status.pending'),
            value: 'PENDING'
          },
          {
            label: getResourceTo('Filter.Route.status.completed'),
            value: 'COMPLETED'
          },
          {
            label: getResourceTo('eventLogHttpAction.status.error'),
            value: 'ERROR'
          }
        ]
      }
    }
  ];

  const handleRowClick = (_, row) => {
    const rowData = row.getData();

    dispatch(actionCreators.openModal({
      modalKey: 'dataSyncLogDetails',
      title: getResourceTo('IntegrationLogModal.Title'),
      data: rowData
    }));
  };

  const getParsedPayload = (values) => {
    const payload = {};

    for (const key in values) {
      const value = values[key];

      if ([undefined, ''].includes(value)) {
        continue;
      }

      if (['startProcessDate', 'endProcessDate'].includes(key)) {
        payload[key] = value.toISOString();
      } else {
        payload[key] = value;
      }
    }

    return payload;
  };

  const getDataSyncLogs = async (page) => {
    const response = await defaultFetch('/ArchiveProcessor/Logs', {
      page,
      limit: 25,
      ...filters.current
    }, 'POST');

    return response;
  };

  const handleApplyFilters = async (values) => {
    tableRef.current?.setLoading?.(true);
    tableRef.current?.resetPagination?.();
    tableRef.current?.clearData?.();

    const payload = getParsedPayload(values);

    setFilters(payload);

    getDataSyncLogs(0)
      .then((response) => {
        tableRef.current?.setData(response);
      })
      .finally(() => {
        tableRef.current?.setLoading?.(false);
      });
  };

  return (
    <Container data-testid="data-sync-logs-list">
      <FilterBuilder
        defaultSelected={['startProcessDate', 'endProcessDate', 'statusProcess']}
        defaultValues={{
          startProcessDate: new Date(),
          endProcessDate: moment().subtract(2, 'days').toDate()
        }}
        onSubmit={handleApplyFilters}
        fields={filterBuilderFields}
      />
      <Flex className="oneface-table-container">
        <Table
          ref={tableRef}
          events={{
            rowClick: handleRowClick
          }}
          headerOptions={{
            showResults: true,
            actions: {
              customizeColumns: true
            }
          }}
          pagination={{
            mode: 'remote',
            type: 'scroll',
            pageSize: 25
          }}
          columns={tableColumns}
          columnsPreference={tableColumns}
          requestData={async (page) => {
            const response = await getDataSyncLogs(page - 1);

            return response;
          }}
        />
      </Flex>
      <DataSyncLogDetailsModal />
    </Container>
  );
}
